'use client';

import { Fragment, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { Term } from '@/interfaces/service/term.interface';
import { CONTACT } from '@/constants/info.constants';
import { BASE_URL } from '@/constants/path.constants';
import { TERMS } from '@/constants/term.constants';
import FlexBox from '@/components/FlexBox';
import TermDetailModal from '@/components/modal/TermDetailModal';
import theme from '@/app/theme';

const Footer = ({ isDark = false }: { isDark?: boolean }) => {
  const fontColor = theme.palette.gray[isDark ? 100 : 400];
  const descriptions = ['(주)리걸케어', '대표 : 김용범', `고객센터 : ${CONTACT}`, '사업자번호 199-88-01627', '주소 : 대전광역시 유성구 대덕대로 582', `문의메일 : cs@legalcare.ai`];
  const terms: Term[] = [TERMS.PRIVACY_POLICY, TERMS.USING_SERVICE];
  const [showTermModal, setShowTermModal] = useState(false);
  const [termDetailModalProps, setTermDetailModalProps] = useState<{
    termDetailUrl: string;
    modalTitle: string;
  }>({ termDetailUrl: '', modalTitle: '' });

  const termsOpenHandler = (e: React.MouseEvent<HTMLAnchorElement>, term: Term) => {
    e.preventDefault();
    setTermDetailModalProps({
      termDetailUrl: term.localUrl ?? term.src ?? '',
      modalTitle: term.koreanTitle
    });
    setShowTermModal(true);
  };

  return (
    <section className="w-full bg-[#667085]">
      <Box
        component="footer"
        sx={{
          width: 1,
          maxWidth: '1440px',
          padding: {
            xs: '40px 20px',
            sm: '40px 80px 80px 80px'
          },
          margin: '0 auto',
          color: fontColor
        }}
      >
        <Typography variant="h3" sx={{ marginBottom: '12px', color: theme.palette.gray[100] }}>
          (주)리걸케어
        </Typography>
        <FlexBox style={{ justifyContent: 'left', flexWrap: 'wrap', color: theme.palette.gray[100] }}>
          <nav className="mb-[10px]">
            <ul className="fs flex items-center justify-start gap-1 text-[#46474C] font-medium underline-offset-[3px]" style={{ '--fs': 12, '--lh': 16 } as React.CSSProperties}>
              <li>
                <a href="/terms/privacy-policy.html" onClick={(e) => termsOpenHandler(e, terms[0])} className="cursor-pointer font-bold text-gray-100">
                  개인정보처리방침
                </a>
              </li>
              <span style={{ color: theme.palette.gray[100], fontWeight: 500 }}>|</span>
              <li>
                <a href="/terms/using-service.html" onClick={(e) => termsOpenHandler(e, terms[1])} className="cursor-pointer font-bold text-gray-100">
                  이용약관
                </a>
              </li>
            </ul>
          </nav>
        </FlexBox>
        <FlexBox style={{ justifyContent: 'left', flexWrap: 'wrap', gap: '6px' }}>
          {descriptions.map((description, i) => (
            <Typography
              variant="subtitle2"
              key={`term=${i}`}
              sx={{
                height: '24px',
                color: theme.palette.gray[100],
                fontWeight: 500,
                '&::after':
                  i < descriptions.length - 1
                    ? {
                        content: '"|"',
                        paddingLeft: '6px',
                        display: 'inline-block'
                      }
                    : {}
              }}
            >
              {description}
            </Typography>
          ))}
        </FlexBox>
        <Typography
          variant="body1"
          sx={{
            marginTop: {
              xs: '12px',
              sm: '32px'
            },
            color: theme.palette.gray[300]
          }}
        >
          Copyright © LEGALCARE All rights reserved.
        </Typography>
      </Box>
      <TermDetailModal showModal={showTermModal} handleShowModal={() => setShowTermModal(!showTermModal)} {...termDetailModalProps} />
    </section>
  );
};

export default Footer;
